import { useState, useEffect, useRef, useCallback } from 'react';
import { BRRApiClient } from '../services/api';

interface VendorBatchResult {
  vendorBatchId: string;
}

interface UseVendorBatchSearchOptions {
  apiClient: BRRApiClient;
  query: string;
  debounceMs?: number;
  minQueryLength?: number;
}

interface UseVendorBatchSearchReturn {
  results: VendorBatchResult[];
  loading: boolean;
  error: Error | null;
}

export const useVendorBatchSearch = ({
  apiClient,
  query,
  debounceMs = 400,
  minQueryLength = 3,
}: UseVendorBatchSearchOptions): UseVendorBatchSearchReturn => {
  const [results, setResults] = useState<VendorBatchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const search = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < minQueryLength) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.searchVendorBatches(searchQuery);
      setResults(response.results);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [apiClient, minQueryLength]);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (query.length < minQueryLength) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    timeoutRef.current = window.setTimeout(() => {
      void search(query);
    }, debounceMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [query, debounceMs, minQueryLength, search]);

  return { results, loading, error };
};
